import type { FeatureRow } from 'app/components/feature-toggles/featureToggleModel'
import type { ReleaseCard, ReleaseGap, ReleaseGroups, ToggleRelease } from 'app/types/feature-toggle-release'

type Audience = { everyone: boolean; workspaceIds: Set<string>; userIds: Set<string> }

// Only customer users count: a staff override (or a user whose email is still
// loading) must never make the rest of a release look behind.
function audienceOf(row: FeatureRow, customerUserIds: ReadonlySet<string>): Audience {
  const ids = (records: FeatureRow[`workspaceOverrides`]) =>
    records.map((r) => r.typeId).filter((id): id is string => !!id)
  return {
    everyone: row.state === `global`,
    workspaceIds: new Set(ids(row.workspaceOverrides)),
    userIds: new Set(ids(row.userOverrides).filter((id) => customerUserIds.has(id))),
  }
}

function gapBetween(audience: Audience, widest: Audience): ReleaseGap | null {
  if (audience.everyone) return null
  if (widest.everyone) return { everyone: true, workspaceIds: [], userIds: [] }
  const workspaceIds = [...widest.workspaceIds].filter((id) => !audience.workspaceIds.has(id))
  const userIds = [...widest.userIds].filter((id) => !audience.userIds.has(id))
  return workspaceIds.length || userIds.length ? { everyone: false, workspaceIds, userIds } : null
}

function buildCard(
  slug: string,
  release: ToggleRelease,
  rows: FeatureRow[],
  customerUserIds: ReadonlySet<string>,
): ReleaseCard {
  const sorted = [...rows].sort((a, b) => a.label.localeCompare(b.label))
  const audiences = sorted.map((row) => audienceOf(row, customerUserIds))
  const widest: Audience = {
    everyone: audiences.some((a) => a.everyone),
    workspaceIds: new Set(audiences.flatMap((a) => [...a.workspaceIds])),
    userIds: new Set(audiences.flatMap((a) => [...a.userIds])),
  }
  const toggles = sorted.map((row, i) => ({ row, gap: gapBetween(audiences[i], widest) }))
  const onCount = toggles.filter((t) => t.row.state !== `off` && t.gap === null).length
  return { slug, release, toggles, onCount }
}

export function groupFeatureRowsByRelease(
  rows: FeatureRow[],
  releases: Readonly<Record<string, ToggleRelease>>,
  releaseOf: (name: string) => string | null | undefined,
  customerUserIds: ReadonlySet<string>,
): ReleaseGroups {
  const rowsBySlug = new Map<string, FeatureRow[]>(Object.keys(releases).map((slug) => [slug, []]))
  const unassigned: FeatureRow[] = []
  for (const row of rows) {
    const members = rowsBySlug.get(releaseOf(row.name) ?? ``)
    if (members) members.push(row)
    else unassigned.push(row)
  }

  const cards = [...rowsBySlug.entries()]
    .map(([slug, members]) => buildCard(slug, releases[slug], members, customerUserIds))
    .sort((a, b) => b.release.started.localeCompare(a.release.started) || a.release.name.localeCompare(b.release.name))
  const isReleased = (card: ReleaseCard) =>
    card.toggles.length > 0 && card.toggles.every((t) => t.row.state === `global`)

  return {
    inProgress: cards.filter((card) => !isReleased(card)),
    unassigned: unassigned.sort((a, b) => a.label.localeCompare(b.label)),
    released: cards.filter(isReleased),
  }
}

// Archived toggles are read-only, and a toggle on for everyone already reaches the user.
export function releaseTogglesToGive(rows: FeatureRow[], userId: string): string[] {
  return rows
    .filter((row) => !row.archived && row.state !== `global` && !row.userOverrides.some((o) => o.typeId === userId))
    .map((row) => row.name)
}
